import React, { useState } from 'react';
import { MessageCircle, MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';
import { contactInfo } from '../mock';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const c = contactInfo;
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({
        title: 'Falta información',
        description: 'Completá al menos nombre y email para continuar.',
      });
      return;
    }
    toast({
      title: `¡Gracias, ${form.name}!`,
      description: 'Recibimos tu consulta. Te contactamos en menos de 24 hs.',
    });
    setForm({ name: '', email: '', phone: '', interest: '', message: '' });
  };

  return (
    <section id="contacto" className="bg-[#1E3A5F] py-20 md:py-24 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
        <div className="text-white">
          <span className="inline-block text-[11px] font-semibold tracking-widest text-[#E8B84A] px-3 py-1 border border-[#E8B84A]/40 rounded-full">
            {c.eyebrow}
          </span>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl leading-[1.05] font-bold">
            {c.titleLine1}
            <br />
            {c.titleLine2}
            <br />
            {c.titleLine3Start}{' '}
            <span className="text-[#9B2C3B] italic">{c.titleLine3End}</span>
          </h2>
          <p className="mt-6 text-[14.5px] text-white/70 leading-relaxed max-w-md">
            {c.description}
          </p>

          <ul className="mt-8 space-y-4 text-[13.5px]">
            {[
              { icon: MapPin, label: 'Dirección', value: c.address },
              { icon: Phone, label: 'Teléfono', value: c.phone },
              { icon: Mail, label: 'Email', value: c.email },
              { icon: Clock, label: 'Horarios', value: c.hours },
            ].map((item) => (
              <li key={item.label} className="flex items-start gap-3">
                <span className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-4 w-4 text-[#E8B84A]" />
                </span>
                <div>
                  <div className="text-white/50 text-[11px] uppercase tracking-wider">
                    {item.label}
                  </div>
                  <div>{item.value}</div>
                </div>
              </li>
            ))}
          </ul>

          <a
            href={`https://wa.me/${c.whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white text-[13px] font-semibold px-5 py-3 rounded-full transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            {c.whatsappCta}
          </a>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl relative">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-serif text-2xl font-bold text-[#1E3A5F]">
                {c.formTitle}
              </h3>
              <p className="text-[12.5px] text-[#1E3A5F]/60 mt-1">{c.formSub}</p>
            </div>
            <span className="h-9 w-9 rounded-full bg-[#EBC9CE] flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-[#9B2C3B]" />
            </span>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] font-semibold text-[#1E3A5F]">Nombre</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Tu nombre"
                  className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-[#FAF6EC]/60 border border-[#1E3A5F]/10 text-[13.5px] text-[#1E3A5F] placeholder:text-[#1E3A5F]/40 focus:outline-none focus:border-[#9B2C3B] transition-colors"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#1E3A5F]">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-[#FAF6EC]/60 border border-[#1E3A5F]/10 text-[13.5px] text-[#1E3A5F] placeholder:text-[#1E3A5F]/40 focus:outline-none focus:border-[#9B2C3B] transition-colors"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] font-semibold text-[#1E3A5F]">Teléfono</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+598 9..."
                  className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-[#FAF6EC]/60 border border-[#1E3A5F]/10 text-[13.5px] text-[#1E3A5F] placeholder:text-[#1E3A5F]/40 focus:outline-none focus:border-[#9B2C3B] transition-colors"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#1E3A5F]">Me interesa</label>
                <Select
                  value={form.interest}
                  onValueChange={(v) => setForm({ ...form, interest: v })}
                >
                  <SelectTrigger className="mt-1.5 bg-[#FAF6EC]/60 border-[#1E3A5F]/10 rounded-xl text-[13.5px] py-2.5 h-auto text-[#1E3A5F]">
                    <SelectValue placeholder="Elegí un curso" />
                  </SelectTrigger>
                  <SelectContent>
                    {c.interests.slice(1).map((i) => (
                      <SelectItem key={i} value={i}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-[12px] font-semibold text-[#1E3A5F]">Mensaje</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="¿En qué podemos ayudarte?"
                rows={4}
                className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-[#FAF6EC]/60 border border-[#1E3A5F]/10 text-[13.5px] text-[#1E3A5F] placeholder:text-[#1E3A5F]/40 focus:outline-none focus:border-[#9B2C3B] transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#9B2C3B] hover:bg-[#822432] text-white text-[14px] font-semibold py-3.5 rounded-full transition-colors"
            >
              {c.submitCta}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>

      {/* Google Maps - interactive embed */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 mt-12">
        <div className="flex items-end justify-between mb-4">
          <div>
            <span className="inline-block text-[11px] font-semibold tracking-widest text-[#E8B84A] px-3 py-1 border border-[#E8B84A]/40 rounded-full">
              CÓMO LLEGAR
            </span>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl font-bold text-white">
              Encontranos en{' '}
              <span className="text-[#E8B84A] italic">Perez Butler.</span>
            </h3>
          </div>
          <a
            href="https://www.google.com/maps?q=-34.80379463677889,-55.907397711701734"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 text-[13px] font-semibold text-[#E8B84A] hover:text-white transition-colors"
          >
            Abrir en Google Maps
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <div className="relative rounded-3xl overflow-hidden border-2 border-[#E8B84A]/30 shadow-2xl bg-white">
          <iframe
            title="Ubicación Pinar English Academy"
            src="https://www.google.com/maps?q=-34.80379463677889,-55.907397711701734&z=18&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, display: 'block' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          {/* Address overlay */}
          <div className="absolute bottom-4 left-4 max-w-[92%] bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg border border-[#E8B84A]/40 flex items-center gap-3 pointer-events-none">
            <span className="h-9 w-9 rounded-full bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
              <MapPin className="h-4 w-4 text-[#E8B84A]" />
            </span>
            <div>
              <div className="text-[10px] font-semibold tracking-widest text-[#9B2C3B]">
                PINAR ENGLISH ACADEMY
              </div>
              <div className="text-[13px] text-[#1E3A5F] font-medium">
                {c.address}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
